import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';
import { getReferenceValueSheets } from 'src/app/reference-value-sheet/store/reference-value-sheet.selectors';
import { Barn } from 'src/app/settings/models/barn.model';
import { getBarns } from 'src/app/settings/store/selectors/setting.selector';

import { ReferenceValueSheet } from '../../../reference-value-sheet/models/reference-value-sheet.model';
import { BREEDS } from '../../models/breeds';
import { Breed, Gender, Section } from '../../models/cycle.model';
import { setSelectedSectionId } from '../../store/actions/cycle.actions';
import { getSelectedCycle, getSelectedSection } from '../../store/selectors/cycle.selectors';

export interface SectionItemDialogResultData {
  section: Section;
}

export interface SectionItemDialogInputData extends AbstractFormDialogData {
  sectionId?: number;
}

@Component({
  selector: 'app-section-item-dialog',
  templateUrl: './section-item-dialog.component.html',
  styleUrls: ['./section-item-dialog.component.scss'],
  imports: [
    NgIf,
    FormDialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    NgFor,
    MatOption,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class SectionItemDialogComponent extends AbstractFormDialog<SectionItemDialogResultData> {
  readonly barns$ = this.store.select(getBarns);
  readonly selectedCycle$ = this.store.select(getSelectedCycle);
  sectionId = 0;
  readonly breeds: Breed[] = BREEDS;
  readonly genders: Gender[] = [Gender.Male, Gender.Female];
  readonly refvalsheets$ = this.store.select(getReferenceValueSheets).pipe(
    take(1),
    map((refvalsheets: ReferenceValueSheet[]) => refvalsheets.filter((rvs) => rvs.isActive)),
  );

  readonly formGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    surfaceArea: new FormControl<number>(undefined, Validators.required),
    barn: new FormControl(null, Validators.required),
    breed: new FormControl(null, Validators.required),
    gender: new FormControl(null, Validators.required),
    referenceValueSheet: new FormControl(null),
  });
  selectedGender: Gender = undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SectionItemDialogInputData,
    protected readonly mdDialogRef: MatDialogRef<
      SectionItemDialogComponent,
      AbstractDialogResultData<SectionItemDialogResultData>
    >,
    private readonly locale: LocaleService,
    private readonly store: Store<RootState>,
  ) {
    super();

    this.sectionId = this.data.sectionId;
    if (this.sectionId > 0) {
      this.data.title = this.locale.translate('section.edit-section');
      this.store.dispatch(setSelectedSectionId({ id: this.sectionId }));
      this.store
        .select(getSelectedSection)
        .pipe(
          withLatestFrom(this.barns$, this.refvalsheets$),
          skipWhile(
            ([section, barns, _]) =>
              section === null || section === undefined || barns?.length === 0,
          ),
          take(1),
        )
        .subscribe(([section, barns, refvalsheets]) => {
          this.formGroup.controls.barn.setValue(barns.find((p) => p.id === section.barn.id));
          this.formGroup.controls.breed.setValue(
            this.breeds.find((b) => b.id === section.breed.id),
          );
          this.selectedGender = section.gender;
          this.formGroup.controls.gender.setValue(section.gender.toString());
          this.formGroup.controls.name.setValue(section.name);
          this.formGroup.controls.surfaceArea.setValue(section.surfaceArea);
          this.formGroup.controls.referenceValueSheet.setValue(
            refvalsheets.find((p) => p.id === section.referenceValueSheetId),
          );
        });
    } else {
      this.data.title = this.locale.translate('section.add-section');
    }

    this.formGroup.addAsyncValidators(this.surfaceAreaValidator);
  }

  surfaceAreaValidator: AsyncValidatorFn = (
    control: AbstractControl,
  ): Observable<ValidationErrors> | null => {
    const surfaceArea = control.get('surfaceArea');
    const barn = control.get('barn');

    if (surfaceArea?.value && barn?.value?.surfaceArea) {
      return this.selectedCycle$.pipe(
        take(1),
        map((cycle) => {
          const section: Section = {
            id: this.sectionId,
            name: this.formGroup.controls.name.value,
            surfaceArea: parseInt(control.get('surfaceArea').value, 10),
            barn: control.get('barn').value,
            breed: this.formGroup.controls.breed.value,
            gender: this.formGroup.controls.gender.value,
            referenceValueSheetId: (<ReferenceValueSheet>(
              this.formGroup.controls.referenceValueSheet.value
            ))?.id,
          };
          const filteredSections = [
            ...cycle.sections.filter(
              (s) => s.barn.id === section.barn.id && s.id !== this.sectionId,
            ),
            section,
          ];
          return this.isSectionSurfaceAreaValid(filteredSections, section.barn)
            ? null
            : <ValidationErrors>{ surfaceAreaTooLarge: true };
        }),
      );
    } else {
      return of(null);
    }
  };

  private isSectionSurfaceAreaValid(sections: Section[], barn: Barn) {
    return (
      sections.reduce((acc: number, section: Section) => acc + section.surfaceArea, 0) <=
      barn.surfaceArea
    );
  }

  save() {
    this.selectedCycle$.pipe(take(1)).subscribe((cycle) => {
      if (this.formGroup.valid) {
        const section: Section = {
          id: this.sectionId,
          name: this.formGroup.controls.name.value,
          surfaceArea: parseInt(this.formGroup.controls.surfaceArea.value.toString(), 10),
          barn: this.formGroup.controls.barn.value,
          breed: this.formGroup.controls.breed.value,
          gender: this.formGroup.controls.gender.value,
          referenceValueSheetId: (<ReferenceValueSheet>(
            this.formGroup.controls.referenceValueSheet.value
          ))?.id,
        };
        const filteredSections = [
          ...cycle.sections.filter((s) => s.barn.id === section.barn.id && s.id !== this.sectionId),
          section,
        ];
        if (this.isSectionSurfaceAreaValid(filteredSections, section.barn)) {
          this.confirm({ section });
        }
      }
    });
  }
}
